import React, { useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import { StyledTabContainer, SecondaryTab } from "../../../../Components/CustomTab";
import { PrimaryTooltip } from "../../../../Components/StyledComponent";
import StringsPacksGrid from "./Battery/StringsPacksGrid";
import ReplacementLogsTable from "./ReplacementLogs/ReplacementLogsTable";
import { useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import {
    getComponentsTabLabel,
    getSelectionModeCopy,
    SELECTION_MODE_EDIT,
    SELECTION_MODE_REPLACE,
} from "../../../../Helper/DigitalTwin/BatteryComponentsHelper";

const SUB_TABS = [
    { id: 0, slug: "battery", name: "Battery" },
    { id: 1, slug: "replacementLogs", name: "Replacement Logs" },
];

// Digital Twin components tab: Battery (strings -> packs grid) and Replacement Logs
// sub-tabs. Renders below the shared DigitalTwinHeader. The heading reads the same
// label as the tab itself, so the two cannot drift.
export default function Components({ batteryID }) {
    const [subTab, setSubTab] = useState("battery");
    const { t } = useTranslation(['digital_twin']);
    const { showBusDeviceInfo } = useBatteryInfo();

    // "Replace Multiple Packs" sits under the Battery sub-tab, on the left above the
    // grid it acts on, but the packs and the bus/permission gate live in the grid
    // below — hence the mode here and the gate reported up from there.
    // null when idle, otherwise which ACTION the selection is being gathered for.
    // A single flag would not do: replace and edit have different permission and
    // bus-status gates, so the grid has to know which rule to apply to the checkboxes.
    const [selectionMode, setSelectionMode] = useState(null);
    const [replaceGate, setReplaceGate] = useState({
        isVisible: false,
        canReplace: false,
        blockedReason: null,
        canEditDetails: false,
    });
    const { isVisible, canReplace, blockedReason, canEditDetails } = replaceGate;

    const exitSelectionMode = useCallback(() => setSelectionMode(null), []);
    // Stable identity: the grid reports the gate from an effect, so a new function
    // each render would re-fire it on every render.
    const handleReplaceGateChange = useCallback((gate) => setReplaceGate(gate), []);

    // Leaving the Battery sub-tab abandons an in-progress selection: the packs it
    // refers to are no longer on screen, so a lingering action bar would be
    // acting on something invisible.
    const handleSubTabChange = (_event, value) => {
        if (value !== "battery") setSelectionMode(null);
        setSubTab(value);
    };

    const replaceMultipleButton = (
        <span>
            <Button
                data-testid="replace-multiple"
                disableElevation
                disabled={!canReplace}
                onClick={() => setSelectionMode(SELECTION_MODE_REPLACE)}
                startIcon={<LayersOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#6D72F6",
                    border: "1px solid #C7C9FB",
                    borderRadius: "8px",
                    padding: "7px 18px",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#F7F7FF", borderColor: "#6D72F6" },
                    "&.Mui-disabled": { color: "#BDBDBD", borderColor: "#E6E6E6" },
                }}
            >
                Replace Multiple Packs
            </Button>
        </span>
    );

    // Edit Multiple Packs sits beside Replace and is gated only on the update
    // permission -- deliberately NOT on the bus being Non-operational, since
    // correcting mistyped details is a data fix rather than a physical act.
    const editMultipleButton = (
        <Button
            data-testid="edit-multiple"
            disableElevation
            disabled={!canEditDetails}
            onClick={() => setSelectionMode(SELECTION_MODE_EDIT)}
            startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                // Identical to replaceMultipleButton above: these are peer actions
                // sitting side by side, so styling one as secondary implied a
                // hierarchy between them that does not exist.
                color: "#6D72F6",
                border: "1px solid #C7C9FB",
                borderRadius: "8px",
                padding: "7px 18px",
                backgroundColor: "#fff",
                "&:hover": { backgroundColor: "#F7F7FF", borderColor: "#6D72F6" },
                "&.Mui-disabled": { color: "#BDBDBD", borderColor: "#E6E6E6" },
            }}
        >
            Edit Multiple Packs
        </Button>
    );

    return (
        <Box sx={{ pt: "20px" }}>
            <Box>
                <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1A1A1A" }}>
                    {getComponentsTabLabel(showBusDeviceInfo(), t)}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#686868", mt: "2px" }}>
                    Packs installed on this bus and the Replacement logs.
                </Typography>
            </Box>

            <Box sx={{ borderBottom: "1px solid #E6E6E6", mt: "16px" }}>
                <StyledTabContainer
                    value={subTab}
                    onChange={handleSubTabChange}
                    aria-label="components sub tabs"
                    elevation={1}
                >
                    {SUB_TABS.map((tab) => (
                        <SecondaryTab key={tab.id} value={tab.slug} label={tab.name} />
                    ))}
                </StyledTabContainer>
            </Box>

            {subTab === "battery" && (
                <>
                    {/* Sits above the grid it acts on, left aligned under the Battery
                        sub-tab, so the control and its targets read as one block. */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            mt: "16px",
                        }}
                    >
                        {selectionMode ? (
                            <Box
                                data-testid="selection-mode-chip"
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#6D72F6",
                                    backgroundColor: "#EEF0FF",
                                    borderRadius: "8px",
                                    padding: "8px 16px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {getSelectionModeCopy(selectionMode).chip}
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", gap: "12px" }}>
                                {!isVisible ? null : canReplace ? (
                                    replaceMultipleButton
                                ) : (
                                    <PrimaryTooltip
                                        title={blockedReason || ""}
                                        placement="top"
                                    >
                                        {replaceMultipleButton}
                                    </PrimaryTooltip>
                                )}
                                {canEditDetails ? editMultipleButton : null}
                            </Box>
                        )}
                    </Box>

                    <StringsPacksGrid
                        batteryID={batteryID}
                        selectionMode={selectionMode}
                        onExitSelectionMode={exitSelectionMode}
                        onReplaceGateChange={handleReplaceGateChange}
                    />
                </>
            )}

            {subTab === "replacementLogs" && <ReplacementLogsTable batteryID={batteryID} />}
        </Box>
    );
}
